import {ApolloError} from "@apollo/client";
import {Alert} from "antd";
import {FormikProps} from "formik";
import unionBy from "lodash/unionBy";
import React, {FC, memo} from "react";
import {Maybe, UpdateCourseAndBookingsInput, useCourseGetInstructorsByLocationQuery, useGetCompanyInfoQuery, useGetInstructorsByIdQuery, useGetInstructorsQuery} from "../../generated/graphql";
import {isDefined} from "../../helpers/typeScriptHelpers";
import i18n from "../../services/i18n";
import LayoutStep from "../../views/LayoutStep/LayoutStep";
import {AdditionalShortInfoInput, CourseInstructorInput, CourseMaxWaitlistInput, EditRestrictiveBookingInput, NumberInput, TextAreaInput} from "./CourseViews/CourseStepViews";
import TextAreaInputMarkdown from "../../views/TextAreaInputMarkdown/TextAreaInputMarkdown";
import {PossibleFormikPropsType} from "./CourseCreate/CourseCreateForm";


type StepSettingsProps = {
    formikProps: FormikProps<PossibleFormikPropsType>;
    error: ApolloError | Error | undefined;
    courseNumber?: string;
    nextStepButton: React.ReactElement;
    subtitle?: Maybe<string> | React.ReactNode;
    hasBilledLessons?: boolean;
};

const StepSettings: FC<StepSettingsProps> = ({formikProps, error, courseNumber, nextStepButton, subtitle, hasBilledLessons = false}) => {
    const isEditCourseInput = (input: PossibleFormikPropsType): input is UpdateCourseAndBookingsInput => {
        return (input as UpdateCourseAndBookingsInput).id !== undefined;
    };

    const {values, initialValues} = formikProps;
    const {data: companyInfoData} = useGetCompanyInfoQuery({
        fetchPolicy: "cache-first",
    });
    const featureAssistantApp = companyInfoData?.companyInfo?.subscription?.featureAssistantApp;

    const initialInstructorIds = initialValues?.instructors?.map(({id}) => id);

    const {data: initialInstructorData, loading: initialInstructorLoading} = useGetInstructorsByIdQuery({
        variables: {
            ids: initialInstructorIds ?? [],
        },
        skip: initialInstructorIds === undefined,
    });

    const {data: instructorsForLocationResponse, loading} = useCourseGetInstructorsByLocationQuery({
        skip: isDefined(values.locationId) === false,
        variables: {
            locationId: values.locationId ?? "",
        },
    });

    // get all instructors if no location has been selected
    const {data: allInstructorsResponse, loading: loadingAllInstructors} = useGetInstructorsQuery({
        skip: isDefined(values.locationId) === true,
    });

    const instructorsLoading = loadingAllInstructors || loading || initialInstructorLoading;

    // Inaktive nicht mehr zur Auswahl anbieten; bereits ausgewählte bleiben sichtbar, sonst zeigt der Select nur die ID
    const selectedInstructorIds = values.instructors?.map(({id}) => id) ?? [];
    const isSelectable = ({id, active}: {id: string; active: boolean}) => active || selectedInstructorIds.includes(id);

    const instructorsForLocation = instructorsForLocationResponse?.findInstructorsByLocation ?? [];
    const initialInstructors = initialInstructorData?.findInstructorByIds ?? [];

    const instructors =
        instructorsForLocation.length === 0 && initialInstructors.length === 0
            ? (allInstructorsResponse?.instructors ?? []).filter(isSelectable)
            : unionBy(instructorsForLocation.filter(isSelectable), initialInstructors, "id");

    return (
        <LayoutStep
            columns={16}
            title={i18n.containers.courses.CourseCreate.courseStepSettings.title()}
            subtitle={subtitle}
            button={nextStepButton}
            error={error}
            lessonBooking={Boolean(formikProps.values.allowLessonBooking)}
        >
            {hasBilledLessons && (
                <Alert type="info" showIcon message={i18n.containers.courses.CourseEdit.billedLessons.settingsDisabled.instructor()} style={{marginBottom: 16, gridColumn: "1 / -1"}} />
            )}
            <CourseInstructorInput
                instructors={instructors}
                loading={instructorsLoading}
                label={i18n.containers.courses.CourseCreate.courseTrainer.placeholder()}
                placeholder={i18n.containers.courses.CourseCreate.courseTrainer.placeholder()}
                formikProps={formikProps}
                testId="CourseInstructorInput"
                style={{gridColumn: "span 8"}}
                disabled={hasBilledLessons}
            />
            <NumberInput
                fieldName="minAttendees"
                label={i18n.containers.courses.CourseCreate.courseAttendeeLimitMin.label()}
                min={1}
                formikProps={formikProps}
                testId="CourseMinAttendeesInput"
                style={{gridColumn: "span 2"}}
                // style={{gridColumn: "span 1", border: "1px dotted red"}}
            />
            <NumberInput
                fieldName="maxAttendees"
                label={i18n.containers.courses.CourseCreate.courseAttendeeLimitMax.label()}
                formikProps={formikProps}
                testId="CourseMaxAttendeesInput"
                style={{gridColumn: "span 2"}}
            />
            <CourseMaxWaitlistInput
                fieldName="maxWaitlist"
                label={i18n.containers.courses.CourseCreate.maxWaitlist.label()}
                formikProps={formikProps}
                testId="CourseMaxWaitlistInput"
                style={{gridColumn: "span 4"}}
                placeholder={i18n.containers.courses.CourseCreate.maxWaitlist.placeholder()}
                isActive={values.maxWaitlist !== null && values.maxWaitlist !== undefined}
            />
            {featureAssistantApp ? (
                <NumberInput
                    fieldName="maxAssistantsPerLesson"
                    label={i18n.containers.courses.CourseCreate.maxAssistantsPerLesson.label()}
                    placeholder={i18n.containers.courses.CourseCreate.maxAssistantsPerLesson.placeholder()}
                    min={0}
                    formikProps={formikProps}
                    required={false}
                    testId="CourseMaxAssistantsPerLessonInput"
                    // style={{gridColumn: "span 4"}}
                    style={{gridColumn: "13 / span 4"}}
                />
            ) : null}
            <TextAreaInput
                fieldName="annotation"
                label={i18n.containers.courses.CourseCreate.courseInfo.label()}
                placeholder={i18n.containers.courses.CourseCreate.courseInfo.placeholder()}
                rows={6}
                formikProps={formikProps}
                testId="CourseInfoInput"
                style={{gridColumn: "1 / span 8"}}
            />
            <TextAreaInputMarkdown
                fieldName="websiteInfo"
                label={i18n.containers.courses.CourseCreate.courseWebsiteInfo.label()}
                placeholder={i18n.containers.courses.CourseCreate.courseWebsiteInfo.placeholder()}
                rows={6}
                formikProps={formikProps}
                testId="CourseWebsiteInfoInput"
                style={{gridColumn: "span 8"}}
            />
            <EditRestrictiveBookingInput
                formikProps={formikProps}
                prefixedCourseNumber={courseNumber}
                testId="EditRestrictiveBookingInput"
                courseId={isEditCourseInput(values) ? values.id : undefined}
                style={{gridColumn: "1 / span 8"}}
            />
            <AdditionalShortInfoInput
                fieldName="additionalShortInfo"
                label={i18n.containers.courses.CourseCreate.additionalShortInfo.label()}
                placeholder={i18n.containers.courses.CourseCreate.additionalShortInfo.placeholder()}
                formikProps={formikProps}
                testId="CourseAdditionalShortInfo"
                style={{gridColumn: "9 / span 8"}}
            />
            {/* <AdditionalShortInfoInput
                fieldName="additionalShortInfo"
                label={i18n.containers.courses.CourseCreate.additionalShortInfo.label()}
                placeholder={i18n.containers.courses.CourseCreate.additionalShortInfo.placeholder()}
                formikProps={formikProps}
                testId="CourseAdditionalShortInfo"
                style={{gridColumn: "1 / span 16"}}
            />
            <EditRestrictiveBookingInput
                formikProps={formikProps}
                testId="EditRestrictiveBookingInput"
                courseId={isEditCourseInput(values) ? values.id : undefined}
                style={{gridColumn: "1 / span 16"}}
            /> */}
        </LayoutStep>
    );
};

export default memo(StepSettings);
